/*!
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, ChangeDetectionStrategy, Input, TemplateRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, map, withLatestFrom } from 'rxjs/operators';

import { EPlotType, IBalloon } from '../../../core/models';
import { CloseBalloon } from '../../../core/store/actions';
import { IAppState } from '../../../core/store/state';
import { WindowResizeService } from '../../../../app/core/services';
import { getIsReplayOpen } from '../../../core/store/selectors';

const balloonAnimationState = 'in';
const replayBalloonAnimationState = 'mobile-in';

@Component({
  selector: 'fm-overlay-balloon',
  templateUrl: './overlay-balloon.component.html',
  styleUrls: ['./overlay-balloon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('balloonAnimation', [
      state(balloonAnimationState, style({ transform: 'translateY(0) translateX(-50%)' })),
      state(replayBalloonAnimationState, style({ transform: 'translateY(-90px) translateX(-50%)' })),
      transition(':enter', [style({ transform: 'translateY(100%) translateX(-50%)' }), animate('192ms')]),
      transition(':leave', [animate('192ms', style({ transform: 'translateY(100%) translateX(-50%)' }))])
    ])
  ]
})
export class OverlayBalloonComponent implements OnChanges, OnDestroy {
  @Input()
  public balloon: IBalloon;
  @Input()
  public balloonTemplate: TemplateRef<any>;

  public EPlotType = EPlotType;

  public animationState: string;

  constructor(private readonly _store: Store<IAppState>, private readonly _windowResizeService: WindowResizeService) {}

  private readonly _unsubscribe$ = new Subject<void>();

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.balloon && changes.balloon.currentValue) {
      this._windowResizeService.isMobile$
        .pipe(
          takeUntil(this._unsubscribe$),
          withLatestFrom(this._store.pipe(select(getIsReplayOpen))),
          map(([isMobile, isReplayOpen]) => isMobile && isReplayOpen)
        )
        .subscribe(
          shouldUseReplayAnimation => (this.animationState = shouldUseReplayAnimation ? replayBalloonAnimationState : balloonAnimationState)
        );
    }
  }

  public onBalloonClosed(): void {
    this._store.dispatch(new CloseBalloon());
  }
}
